export type ClientMetricName =
  | 'LCP'
  | 'INP'
  | 'CLS'
  | 'FCP'
  | 'TTFB'
  | 'COLD_START'
  | 'WARM_START'
  | 'CACHE_RESTORE'
  | 'CONTENT_USEFUL'
  | 'QUERY_CACHE_HIT'
  | 'QUERY_CACHE_MISS';

export type ClientMetricEvent = {
  name: ClientMetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor' | 'custom';
  routeFamily: string;
  platform: string;
};

export type ClientMetricReporter = {
  observe: (event: Readonly<ClientMetricEvent>) => void;
  flush: () => Promise<void>;
  dispose: () => void;
};

export type ClientMetricReporterOptions = {
  endpoint: string;
  getAccessToken: () => string | null;
  fetchImpl?: typeof fetch;
  batchSize?: number;
  flushIntervalMs?: number;
  requestTimeoutMs?: number;
};

const MAX_QUEUE = 40;
const KNOWN_ROUTE_SEGMENTS = new Set([
  'about', 'admin', 'app', 'billing', 'company', 'contact', 'conversations',
  'dashboard', 'documents', 'download', 'examples', 'login', 'notifications',
  'pricing', 'profile', 'register', 'settings', 'support', 'verify',
]);
const LOCALE_PATTERN = /^[a-z]{2}(?:-[a-z]{2})?$/i;

/** Returns a stable page family and removes locale, ids, tokens and query data. */
export function toPageRouteFamily(path: string): string {
  const segments = (path.split(/[?#]/, 1)[0] ?? '').split('/').filter(Boolean);
  if (segments[0] && LOCALE_PATTERN.test(segments[0])) segments.shift();
  if (segments.length === 0) return '/';

  const first = segments[0]!.toLowerCase();
  if (!KNOWN_ROUTE_SEGMENTS.has(first)) return '/other';
  if ((first === 'app' || first === 'admin') && segments[1]) {
    const second = segments[1].toLowerCase();
    return KNOWN_ROUTE_SEGMENTS.has(second) ? `/${first}/${second}` : `/${first}/other`;
  }
  return `/${first}`;
}

/** Bounded authenticated reporter for browser/app lifecycle metrics. */
export function createClientMetricReporter(
  options: ClientMetricReporterOptions,
): ClientMetricReporter {
  const batchSize = Math.min(20, Math.max(1, options.batchSize ?? 10));
  const flushIntervalMs = Math.max(250, options.flushIntervalMs ?? 5_000);
  const requestTimeoutMs = Math.max(250, options.requestTimeoutMs ?? 5_000);
  const fetchImpl = options.fetchImpl ?? fetch;
  const queue: ClientMetricEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let flushing = false;
  let disposed = false;

  const schedule = () => {
    if (timer || disposed) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, flushIntervalMs);
  };

  const observe = (event: Readonly<ClientMetricEvent>) => {
    if (disposed || !Number.isFinite(event.value) || event.value < 0) return;
    queue.push({ ...event });
    if (queue.length > MAX_QUEUE) queue.shift();
    if (queue.length >= batchSize) void flush();
    else schedule();
  };

  const flush = async (): Promise<void> => {
    if (disposed || flushing || queue.length === 0) return;
    const token = options.getAccessToken();
    if (!token) return;

    flushing = true;
    const batch = queue.splice(0, batchSize);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);
    try {
      await fetchImpl(options.endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-TraceDocuments-Client-Platform': batch[0]?.platform ?? 'UNKNOWN',
        },
        body: JSON.stringify({ metrics: batch }),
        signal: controller.signal,
        keepalive: true,
      });
    } catch {
      // Best-effort only. Drop failed telemetry rather than retrying aggressively.
    } finally {
      clearTimeout(timeoutId);
      flushing = false;
      if (queue.length > 0) schedule();
    }
  };

  const dispose = () => {
    disposed = true;
    if (timer) clearTimeout(timer);
    timer = null;
    queue.length = 0;
  };

  return { observe, flush, dispose };
}
